import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Injectable } from '@angular/core';
import { FrontendLoggerService } from './frontend-logger.service';
import { DownloadedFile, parseContentDispositionFilename } from './generation/download.util';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly apiUrl = environment.apiUrl;

  constructor(
    private readonly http: HttpClient,
    private readonly frontendLogger: FrontendLoggerService
  ) {}

  // Métodos genéricos para interactuar con la API (siempre adjuntan token cuando está disponible)
  get<T>(endpoint: string, params?: any, headers?: HttpHeaders): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;
    const sanitizedParams = this.sanitizeParams(params);
    this.logHttpRequest('GET', url, this.hasAuthorizationHeader(headers), !!sanitizedParams);
    return this.http.get<T>(url, { params: sanitizedParams, headers }).pipe(
      tap({ next: () => this.logHttpSuccess('GET', url), error: (e) => this.logHttpError('GET', url, e) })
    );
  }

  getWithResponse<T>(endpoint: string, params?: any, headers?: HttpHeaders): Observable<HttpResponse<T>> {
    const url = `${this.apiUrl}/${endpoint}`;
    const sanitizedParams = this.sanitizeParams(params);
    this.logHttpRequest('GET(response)', url, this.hasAuthorizationHeader(headers), !!sanitizedParams);
    return this.http.get<T>(url, {
      params: sanitizedParams,
      headers,
      observe: 'response'
    }).pipe(
      tap({
        next: () => this.logHttpSuccess('GET(response)', url),
        error: (e) => {
          const isExpectedNextAction404 = e?.status === 404 && url.includes('/home-ia/next-action');
          if (!isExpectedNextAction404) {
            this.logHttpError('GET(response)', url, e);
          }
        }
      })
    );
  }

  // GET que espera texto plano (por ejemplo, endpoints que devuelven una URL como string)
  getText(endpoint: string, params?: any): Observable<string> {
    const url = `${this.apiUrl}/${endpoint}`;
    const sanitizedParams = this.sanitizeParams(params);
    this.logHttpRequest('GET(text)', url, false, !!sanitizedParams);
    return this.http.get(url, { params: sanitizedParams, responseType: 'text' }).pipe(
      tap({ next: () => this.logHttpSuccess('GET(text)', url), error: (e) => this.logHttpError('GET(text)', url, e) })
    );
  }

  // GET que espera un blob binario (por ejemplo, descarga/streaming de archivos)
  getBlob(endpoint: string, params?: any): Observable<Blob> {
    const url = `${this.apiUrl}/${endpoint}`;
    const sanitizedParams = this.sanitizeParams(params);
    this.logHttpRequest('GET(blob)', url, false, !!sanitizedParams);
    return this.http.get(url, { params: sanitizedParams, responseType: 'blob' }).pipe(
      tap({ next: () => this.logHttpSuccess('GET(blob)', url), error: (e) => this.logHttpError('GET(blob)', url, e) })
    );
  }

  /**
   * GET de un blob conservando el nombre que decide el SERVIDOR (Content-Disposition).
   * Requiere que el backend exponga esa cabecera vía CORS.
   */
  getBlobWithFilename(endpoint: string, params?: any): Observable<DownloadedFile> {
    const url = `${this.apiUrl}/${endpoint}`;
    const sanitizedParams = this.sanitizeParams(params);
    this.logHttpRequest('GET(blob)', url, false, !!sanitizedParams);
    return this.http
      .get(url, { params: sanitizedParams, responseType: 'blob', observe: 'response' })
      .pipe(
        tap({ next: () => this.logHttpSuccess('GET(blob)', url), error: (e) => this.logHttpError('GET(blob)', url, e) }),
        map((response) => ({
          blob: response.body ?? new Blob(),
          filename: parseContentDispositionFilename(response.headers.get('Content-Disposition')),
        }))
      );
  }

  post<T>(endpoint: string, data: any, headers?: HttpHeaders): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;
    this.logHttpRequest('POST', url, this.hasAuthorizationHeader(headers));
    return this.http.post<T>(url, data, { headers }).pipe(
      tap({ next: () => this.logHttpSuccess('POST', url), error: (e) => this.logHttpError('POST', url, e) })
    );
  }

  put<T>(endpoint: string, data: any, headers?: HttpHeaders): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;
    this.logHttpRequest('PUT', url, this.hasAuthorizationHeader(headers));
    return this.http.put<T>(url, data, { headers }).pipe(
      tap({ next: () => this.logHttpSuccess('PUT', url), error: (e) => this.logHttpError('PUT', url, e) })
    );
  }

  patch<T>(endpoint: string, data: any, headers?: HttpHeaders): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;
    this.logHttpRequest('PATCH', url, this.hasAuthorizationHeader(headers));
    return this.http.patch<T>(url, data, { headers }).pipe(
      tap({ next: () => this.logHttpSuccess('PATCH', url), error: (e) => this.logHttpError('PATCH', url, e) })
    );
  }

  delete<T>(endpoint: string, headers?: HttpHeaders): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;
    this.logHttpRequest('DELETE', url, this.hasAuthorizationHeader(headers));
    return this.http.delete<T>(url, { headers }).pipe(
      tap({ next: () => this.logHttpSuccess('DELETE', url), error: (e) => this.logHttpError('DELETE', url, e) })
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Métodos públicos (sin autenticación) - para endpoints que no requieren token
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * POST público sin intentar obtener token de Auth0
   */
  postPublic<T>(endpoint: string, data: any): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;
    this.logHttpRequest('POST(public)', url, false);
    return this.http.post<T>(url, data).pipe(
      tap({ next: () => this.logHttpSuccess('POST(public)', url), error: (e) => this.logHttpError('POST(public)', url, e) })
    );
  }

  private logHttpRequest(method: string, url: string, hasAuthHeader: boolean, hasParams = false): void {
    this.frontendLogger.debug(`[ApiService] ${method} ${url} hasAuthHeader=${hasAuthHeader} hasParams=${hasParams}`);
  }

  private logHttpSuccess(method: string, url: string): void {
    this.frontendLogger.debug(`[ApiService] ${method} ${url} success`);
  }

  private logHttpError(method: string, url: string, error: unknown): void {
    const status = this.extractStatus(error);
    this.frontendLogger.error(`[ApiService] ${method} ${url} failed status=${status}`);
  }

  private hasAuthorizationHeader(headers?: HttpHeaders): boolean {
    return !!headers?.get('Authorization');
  }

  private extractStatus(error: unknown): string {
    if (typeof error === 'object' && error !== null && 'status' in error) {
      const status = (error as { status?: unknown }).status;
      if (status === undefined || status === null) {
        return 'unknown';
      }
      return typeof status === 'string' || typeof status === 'number' ? String(status) : 'unknown';
    }

    return 'unknown';
  }

  private sanitizeParams(params?: any): any {
    if (!params || typeof params !== 'object' || Array.isArray(params)) {
      return params;
    }

    return Object.entries(params).reduce<Record<string, string | number | boolean>>((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value as string | number | boolean;
      }
      return acc;
    }, {});
  }
}
